import { getAuthCallbackLocation } from '../lib/authCallbackCapture'
import { supabase } from '../lib/supabase'
import { enforceStagingSessionAccess } from '../lib/stagingAccess'

export type AuthCallbackParams = {
  code: string | null
  error: string | null
  errorDescription: string | null
  accessToken: string | null
  refreshToken: string | null
}

export function parseAuthCallbackParams(location: Pick<Location, 'search' | 'hash'>): AuthCallbackParams {
  const search = new URLSearchParams(location.search)
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''))

  return {
    code: search.get('code'),
    error: search.get('error') || hash.get('error'),
    errorDescription: search.get('error_description') || hash.get('error_description'),
    accessToken: hash.get('access_token'),
    refreshToken: hash.get('refresh_token'),
  }
}

async function waitForAuthSession(timeoutMs = 8000, intervalMs = 250) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      return { error: error.message } as const
    }
    if (data.session) {
      return data.session
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return null
}

async function resolveSessionAfterPkceCode(code: string) {
  const { data: existingData } = await supabase.auth.getSession()
  if (existingData.session) {
    return existingData.session
  }

  const { data, error } = await supabase.auth.exchangeCodeForSession(code)
  if (!error && data.session) {
    return data.session
  }

  const { data: retryData } = await supabase.auth.getSession()
  if (retryData.session) {
    return retryData.session
  }

  const waitedSession = await waitForAuthSession(5000)
  if (waitedSession && 'error' in waitedSession) {
    return waitedSession
  }
  if (waitedSession) {
    return waitedSession
  }

  return { error: error?.message ?? 'No session after OAuth code exchange' } as const
}

async function finalizeSessionAccess(
  userId: string,
): Promise<{ ok: true; userId: string } | { ok: false; error: string }> {
  const allowed = await enforceStagingSessionAccess(
    () => supabase.auth.getSession(),
    () => supabase.auth.signOut()
  )
  if (allowed !== true) {
    return { ok: false, error: allowed }
  }
  return { ok: true, userId }
}

export async function completeAuthCallback(
  location: Pick<Location, 'search' | 'hash'> = getAuthCallbackLocation()
): Promise<{ ok: true; userId: string } | { ok: false; error: string }> {
  const params = parseAuthCallbackParams(location)

  if (params.error) {
    return {
      ok: false,
      error: params.errorDescription || params.error,
    }
  }

  if (params.code) {
    const sessionResult = await resolveSessionAfterPkceCode(params.code)
    if ('error' in sessionResult) {
      return { ok: false, error: sessionResult.error }
    }
    if (sessionResult) {
      return finalizeSessionAccess(sessionResult.user.id)
    }
    return { ok: false, error: 'No session after OAuth code exchange' }
  }

  if (params.accessToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: params.accessToken,
      refresh_token: params.refreshToken || '',
    })
    if (error) {
      return { ok: false, error: error.message }
    }
    if (data.session) {
      return finalizeSessionAccess(data.session.user.id)
    }
  }

  const { data: { session }, error: sessionError } = await supabase.auth.getSession()
  if (sessionError) {
    return { ok: false, error: sessionError.message }
  }
  if (session) {
    return finalizeSessionAccess(session.user.id)
  }

  const lateSession = await waitForAuthSession(3000)
  if (lateSession && 'error' in lateSession) {
    return { ok: false, error: lateSession.error }
  }
  if (lateSession) {
    return finalizeSessionAccess(lateSession.user.id)
  }

  return { ok: false, error: 'No auth credentials in callback URL' }
}
